import { postWithAuth } from '@/lib/httpAdapter';
import apiUrl from './api';
import { logtail } from '@/utils/logtail';

const endpoint = apiUrl + 'initiateConnection/';

export const universalComposioInstaller = async ({
  IntegrationName,
  UserUUID,
  OrgUUID,
  SentryAccessToken,
  GcpServiceAccountJson,
  GcpProjectId,
  GcpAuthMode,
  GcpTargetServiceAccountEmail,
  DatadogApiKey,
  DatadogRegion,
  DatadogGenericId,
  PagerDutyApiKey,
  IncidentIoApiKey,
  RedirectUrl,
  CloudId,
  Subdomain,
  MixpanelServiceAccountSecret,
  MixpanelServiceAccountUsername,
  MixpanelRegion,
  FirefliesApiKey,
  GranolaApiKey,
  ApiKey,
  NewRelicRegion,
  BetterStackQueryApiPassword,
  ElkBaseUrl,
  ElkApiKey,
  ElkIndexPattern,
  GrafanaApiKey,
  GrafanaBaseUrl,
  GrafanaBearerToken,
  AwsAccessKeyId,
  AwsSecretAccessKey,
  AwsRegion,
  AzureTenantId,
  AzureClientId,
  AzureClientSecret,
  AzureSubscriptionId,
  AxiomOrgId,
  AxiomApiHost,
  SumologicAccessId,
  SumologicAccessKey,
  SumologicDeployment,
}: {
  IntegrationName: string;
  UserUUID: string;
  OrgUUID: string;
  SentryAccessToken?: string;
  /** GCP Cloud Logging: full service-account JSON key (raw text the user pasted). */
  GcpServiceAccountJson?: string;
  /** GCP Cloud Logging: project ID whose log entries we should read. */
  GcpProjectId?: string;
  /** GCP Cloud Logging: auth mode — 'sa_key' (default) or 'impersonation'. */
  GcpAuthMode?: string;
  /** GCP Cloud Logging: target SA email for impersonation mode. */
  GcpTargetServiceAccountEmail?: string;
  DatadogApiKey?: string;
  /** Datadog site/region (e.g. us1, eu, us3, us5). Defaults to us1 on backend if omitted. */
  DatadogRegion?: string;
  /** Datadog Application Key / generic_id (Composio API Key schema). Optional. */
  DatadogGenericId?: string;
  /** PagerDuty API key (Composio API_KEY auth); omit for OAuth. */
  PagerDutyApiKey?: string;
  /** Incident.io API key (Composio API_KEY auth). */
  IncidentIoApiKey?: string;
  RedirectUrl?: string;
  CloudId?: string; // For Jira cloud ID
  Subdomain?: string; // For Jira subdomain (e.g., "yourcompany" for "yourcompany.atlassian.net")
  MixpanelServiceAccountSecret?: string;
  MixpanelServiceAccountUsername?: string;
  MixpanelRegion?: string;
  FirefliesApiKey?: string;
  GranolaApiKey?: string;
  ApiKey?: string;
  NewRelicRegion?: string;
  /** Better Stack: optional Query API password for log queries (stored in Composio) */
  BetterStackQueryApiPassword?: string;
  /** ELK: full Elasticsearch cluster URL (stored on IntegrationsModel.subdomain) */
  ElkBaseUrl?: string;
  /** ELK: Elasticsearch API key for authentication (stored on IntegrationsModel.accessToken) */
  ElkApiKey?: string;
  /** ELK: comma-separated index patterns to query (stored on IntegrationsModel.cloudId) */
  ElkIndexPattern?: string;
  /** Grafana service account token (Bearer Token auth via Composio). */
  GrafanaApiKey?: string;
  /** Grafana instance base URL, e.g. https://yourorg.grafana.net */
  GrafanaBaseUrl?: string;
  /** Grafana bearer token for bearer authentication. */
  GrafanaBearerToken?: string;
  /** AWS CloudWatch: IAM access key ID */
  AwsAccessKeyId?: string;
  /** AWS CloudWatch: IAM secret access key */
  AwsSecretAccessKey?: string;
  /** AWS CloudWatch: AWS region */
  AwsRegion?: string;
  /** Azure Monitor: Azure AD tenant ID */
  AzureTenantId?: string;
  /** Azure Monitor: Azure AD app client ID */
  AzureClientId?: string;
  /** Azure Monitor: Azure AD app client secret */
  AzureClientSecret?: string;
  /** Azure Monitor: Azure subscription ID */
  AzureSubscriptionId?: string;
  /** Axiom: optional org id for PAT (`x-axiom-org-id`) */
  AxiomOrgId?: string;
  /** Axiom API base URL, default https://api.axiom.co */
  AxiomApiHost?: string;
  /** Sumo Logic: Access ID from Administration > Security > Access Keys */
  SumologicAccessId?: string;
  /** Sumo Logic: Access Key from Administration > Security > Access Keys */
  SumologicAccessKey?: string;
  /** Sumo Logic: deployment region (e.g. us1, us2, eu, au, ca, in, jp, fed) */
  SumologicDeployment?: string;
}) => {
  try {
    const response = await postWithAuth(
      endpoint,
      {
        IntegrationName,
        UserUUID,
        OrgUUID,
        SentryAccessToken,
        GcpServiceAccountJson,
        GcpProjectId,
        GcpAuthMode,
        GcpTargetServiceAccountEmail,
        DatadogApiKey,
        DatadogRegion,
        DatadogGenericId,
        PagerDutyApiKey,
        IncidentIoApiKey,
        RedirectUrl,
        CloudId,
        Subdomain,
        service_account_secret: MixpanelServiceAccountSecret,
        service_account_username: MixpanelServiceAccountUsername,
        region: MixpanelRegion,
        FirefliesApiKey,
        GranolaApiKey,
        ApiKey,
        new_relic_region: NewRelicRegion,
        BetterStackQueryApiPassword,
        ElkBaseUrl,
        ElkApiKey,
        ElkIndexPattern,
        GrafanaApiKey,
        GrafanaBaseUrl,
        GrafanaBearerToken,
        AwsAccessKeyId,
        AwsSecretAccessKey,
        AwsRegion,
        AzureTenantId,
        AzureClientId,
        AzureClientSecret,
        AzureSubscriptionId,
        AxiomOrgId,
        AxiomApiHost,
        SumologicAccessId,
        SumologicAccessKey,
        SumologicDeployment,
      },
      {
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'POST,OPTIONS',
        },
      }
    );

    if (!response.ok) {
      if (response.status === 403) {
        return { Error: 'Forbidden', Forbidden: true };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    // Some backends return a JSON string instead of an object; normalize both
    return typeof data === 'string' ? JSON.parse(data) : data;
  } catch (error) {
    logtail.error(error + 'error in universalComposioInstaller', {
      IntegrationName,
      UserUUID,
      OrgUUID,
    });
    logtail.flush();
    return { Error: 'Failed to initiate connection' };
  }
};
